import { useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";

export interface ContextBuffer {
  usedTokens: number;
  freeTokens: number;
  totalTokens: number;
  usagePercent: number;
}

/**
 * Subscribes to AG-UI CUSTOM events named "context_buffer" from the agent
 * and exposes the latest token usage as React state.
 *
 * The backend emits events like:
 *   { type: "CUSTOM", name: "context_buffer", value: { used_tokens: N, free_tokens: M } }
 *
 * These flow through the AG-UI subscriber pipeline but are ignored by the
 * assistant-ui runtime aggregator. We tap into them via agent.subscribe().
 */
export function useContextBuffer(agent: HttpAgent): ContextBuffer | null {
  const [buffer, setBuffer] = useState<ContextBuffer | null>(null);

  useEffect(() => {
    const sub = agent.subscribe({
      onCustomEvent: ({ event }) => {
        if (event.name !== "context_buffer") {
          return;
        }
        const { used_tokens, free_tokens } = event.value ?? {};
        if (typeof used_tokens !== "number" || typeof free_tokens !== "number") {
          return;
        }

        const total = used_tokens + free_tokens;
        setBuffer({
          usedTokens: used_tokens,
          freeTokens: free_tokens,
          totalTokens: total,
          usagePercent: total > 0 ? (used_tokens / total) * 100 : 0,
        });
      },
    });

    return () => sub.unsubscribe();
  }, [agent]);

  return buffer;
}
